import { CaretDown, CaretUp, Star } from "@phosphor-icons/react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { InstrumentTypeResponse } from "../../codegen-api";
import { isInstrumentTypeEnabled } from "../../config/features";
import {
  COLORS,
  ICON_COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import {
  dailyChangeFraction,
  formatKnownPrice,
  formatRoiPercent,
  knownPrice,
} from "../../utils/unknownNumbers";
import { IMarket, MarketContext } from "../../contexts/MarketContext";
import { MarketInstrumentContext } from "../../contexts/MarketInstrumentContext";
import { LocalStorageKeyEnum } from "../../enums/localStorage";
import { useGetMarketSummary } from "../../hooks/api/marketSummary/useGetMarketSummary";
import { useGetSpotStatistics } from "../../hooks/api/statistics/useGetSpotStatistics";
import usePointerSort from "../../hooks/usePointerSort";
import useWatchlist from "../../hooks/useWatchlist";
import { AssetResponse } from "../../utils/asset";
import { instrumentLabel } from "../../utils/instrumentLabel";
import { spotDailyChange } from "../CommandModal/CommandModal.sections";
import { reorderWatchlistSubset } from "../../pages/WatchlistPage/watchlistRows";
import HorizontalScroller from "../shared/HorizontalScroller";
import TickerLogo from "../shared/TickerLogo";
import {
  CollapseToggle,
  CollapsedBar,
  StripWrapper,
  Tile,
  TileChange,
  TilePrice,
  TileSymbol,
} from "./style";

function loadCollapsed(): boolean {
  try {
    return (
      localStorage.getItem(LocalStorageKeyEnum.WATCHLIST_STRIP_COLLAPSED) ===
      "1"
    );
  } catch {
    return false;
  }
}

function WatchlistTickerStrip() {
  const { market, setMarket } = useContext(MarketContext);
  const { getMarketPrecision, activeSpotMarkets } = useContext(
    MarketInstrumentContext
  );
  const { watchlist, updateWatchlist } = useWatchlist();
  // Tiles for every enabled vertical; a build without perps fetches no perp
  // summary, and spot tiles price from the spot markets + `/statistics`.
  const perpsEnabled = isInstrumentTypeEnabled(InstrumentTypeResponse.Perpetual);
  const { data: marketsSummaryData } = useGetMarketSummary(!perpsEnabled);
  const watchedSpotAssets = useMemo(
    () =>
      watchlist
        .filter((w) => w.derivative === InstrumentTypeResponse.Spot)
        .map((w) => w.asset as string),
    [watchlist]
  );
  const { data: spotStatistics } = useGetSpotStatistics(watchedSpotAssets);

  const [collapsed, setCollapsed] = useState<boolean>(loadCollapsed());

  useEffect(() => {
    try {
      localStorage.setItem(
        LocalStorageKeyEnum.WATCHLIST_STRIP_COLLAPSED,
        collapsed ? "1" : "0"
      );
    } catch {
      // ignore
    }
  }, [collapsed]);

  const tiles = useMemo(() => {
    if (watchlist.length === 0) return [];
    const summaryByAsset: Record<string, any> = {};
    (marketsSummaryData?.summaries || []).forEach((s) => {
      summaryByAsset[s.asset as string] = s;
    });
    return watchlist
      .map((w) => {
        if (w.derivative === InstrumentTypeResponse.Perpetual) {
          const s = summaryByAsset[w.asset];
          if (!s) return null;
          // `/markets-summary` sends null for an unknown index/mark. Unknown
          // stays unknown: no $0.00 tile, and the change needs a real 24h
          // reference (index minus the MARK change mixed two prices and
          // "succeeded" on null).
          return {
            asset: w.asset as AssetResponse,
            derivative: w.derivative,
            price: knownPrice(s.index_price),
            changePercent: dailyChangeFraction(
              s.perpetual_info?.mark_daily_change,
              s.perpetual_info?.mark_price_24h_ago
            ),
          };
        }
        if (w.derivative === InstrumentTypeResponse.Spot) {
          const m = activeSpotMarkets?.find(
            (sm) => sm.underlying_asset === w.asset
          );
          if (!m) return null;
          const stats = spotStatistics?.[w.asset];
          // Price from the polled /statistics mark first: /markets is fetched
          // once (immutable), so its mark_price is the price at page load while
          // the % beside it moves.
          const price =
            knownPrice(stats?.mark_price as string | undefined) ??
            knownPrice(m.mark_price);
          return {
            asset: w.asset as AssetResponse,
            derivative: w.derivative,
            price,
            changePercent: spotDailyChange(stats, price),
          };
        }
        return null;
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [activeSpotMarkets, marketsSummaryData?.summaries, spotStatistics, watchlist]);

  const onTileClick = useCallback(
    (asset: AssetResponse, derivative: InstrumentTypeResponse) => {
      setMarket({ asset, derivative });
    },
    [setMarket]
  );

  // Indices are positions among the TILES; entries without a tile (not
  // loaded, another vertical) keep their place in the saved list.
  const onReorderTiles = useCallback(
    (fromIdx: number, toIdx: number) => {
      const subset: IMarket[] = tiles.map((tile) => ({
        asset: tile.asset,
        derivative: tile.derivative,
      }));
      const next = reorderWatchlistSubset(watchlist, subset, fromIdx, toIdx);
      if (next !== watchlist) updateWatchlist(next);
    },
    [tiles, watchlist, updateWatchlist]
  );

  // Pointer-events drag: works for mouse, touch, and pen.
  const sort = usePointerSort({ onReorder: onReorderTiles });

  if (tiles.length === 0) {
    return null;
  }

  if (collapsed) {
    return (
      <CollapsedBar>
        <span>
          <Star size={12} weight="fill" color={COLORS.highlight.one} />
          {tiles.length} in watchlist
        </span>
        <CollapseToggle
          type="button"
          aria-label="Show watchlist"
          onClick={() => setCollapsed(false)}
        >
          <CaretDown size={14} color={ICON_COLORS.two} />
        </CollapseToggle>
      </CollapsedBar>
    );
  }

  return (
    <StripWrapper>
      <HorizontalScroller>
        {tiles.map((tile, index) => {
          const changeKnown = tile.changePercent !== undefined;
          const positive = changeKnown && tile.changePercent! >= 0;
          const pricePrecision = getMarketPrecision(
            tile.asset,
            tile.derivative
          ).price_precision;
          const active =
            market.asset === tile.asset &&
            market.derivative === tile.derivative;
          return (
            <Tile
              key={`${tile.derivative}-${tile.asset}`}
              type="button"
              active={active}
              dragging={sort.dragIndex === index}
              dragOver={sort.overIndex === index && sort.dragIndex !== index}
              onClick={() => onTileClick(tile.asset, tile.derivative)}
              {...sort.getItemProps(index)}
            >
              <TickerLogo asset={tile.asset} size={20} />
              <TileSymbol>
                {instrumentLabel(tile.asset, tile.derivative)}
              </TileSymbol>
              <TilePrice>
                {formatKnownPrice(tile.price, Math.max(pricePrecision || 2, 2))}
              </TilePrice>
              {changeKnown ? (
                <TileChange positive={positive}>
                  {formatRoiPercent(tile.changePercent)}
                </TileChange>
              ) : (
                // Neutral, not green: an unknown change is not "up".
                <TileChange positive style={{ color: TEXT_COLORS.three }}>
                  -
                </TileChange>
              )}
            </Tile>
          );
        })}
      </HorizontalScroller>
      <CollapseToggle
        type="button"
        aria-label="Hide watchlist"
        onClick={() => setCollapsed(true)}
        title="Hide watchlist strip"
      >
        <CaretUp size={14} color={ICON_COLORS.two} />
      </CollapseToggle>
    </StripWrapper>
  );
}

export default WatchlistTickerStrip;
